import type { SilentRequest } from "@azure/msal-browser";
import { InteractionType } from "@azure/msal-browser";
import { AuthenticationProvider } from "@tql/auth-react";
import { useEffect } from "react";
import { Navigate, useLocation } from "react-router";

import {
	loginRequest,
	msalInstance,
	POST_AUTHENTICATED_USER,
} from "~/app/main/auth-config.ts";
import { routes } from "~/app/routes/web-routes.ts";
import { LoadingSpinner } from "~/shared/components/loading-spinner/loading-spinner.tsx";
import { useDeploymentType } from "~/shared/hooks/use-deployment-type.ts";
import { B2CAuthProvider } from "~/shared/utils/b2c-auth.tsx";
import { B2CLoginRedirect } from "~/shared/utils/b2c-login-redirect.tsx";

export function AuthRedirect() {
	const { isInternal } = useDeploymentType();
	const location = useLocation();

	useEffect(() => {
		// Save the current path if it's not root or the auth callback itself
		if (
			location.pathname !== routes.root &&
			location.pathname !== routes.authCallback
		) {
			globalThis.sessionStorage.setItem(
				POST_AUTHENTICATED_USER,
				location.pathname + location.search,
			);
		}
	}, [location]);

	const postAuthPath = globalThis.sessionStorage.getItem(
		POST_AUTHENTICATED_USER,
	);
	const targetPath = postAuthPath || routes.root;

	const auth = isInternal ? (
		<AuthenticationProvider
			apiTokenRequest={
				{
					scopes: [""],
				} as SilentRequest
			}
			instance={msalInstance}
			interactionType={InteractionType.Redirect}
			loading={<LoadingSpinner />}
			uiAuthenticationRequest={loginRequest}
			unauthenticated={<LoadingSpinner />}
		>
			<PostAuthNavigator to={targetPath} />
		</AuthenticationProvider>
	) : (
		<B2CAuthProvider unauthenticated={<B2CLoginRedirect />}>
			<PostAuthNavigator to={targetPath} />
		</B2CAuthProvider>
	);

	return auth;
}

function PostAuthNavigator({ to }: Readonly<{ to: string }>) {
	useEffect(() => {
		globalThis.sessionStorage.removeItem(POST_AUTHENTICATED_USER);
	}, []);

	return <Navigate replace to={to} />;
}
